/**
 * Goal Calculator — wizard to set a savings goal with presets and projection.
 */
import React from 'react';
import { Target, Home, Car, Plane, Palmtree, Users, DollarSign, Calculator, Calendar, Shield, ArrowRight, Sparkles, TrendingDown, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import InstitutionRecommendations from './InstitutionRecommendations';
import { goalPresets } from '../../data/mockData';
import './GoalCalculator.css';

const PRESET_ICONS = { Home, Car, Plane, Palmtree, Users, DollarSign };

const GoalCalculatorCard = ({
  goalData,
  setGoalData,
  selectGoalPreset,
  calculateGoal,
  scrollToSection
}) => (
  <div className="goal-calculator-container">
    <Card className="goal-calculator-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl">
          <Target className="w-8 h-8 text-neon" />
          Calculadora de Meta de Ahorro
        </CardTitle>
        <CardDescription className="text-base">
          Define tu objetivo HOY, antes de que se convierta en otra historia que cuentas en la cena de Navidad 🎯
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="goal-grid">
          <div className="goal-inputs">
            <div>
              <label className="input-label">¿Qué quieres lograr?</label>
              <div className="goal-presets">
                {goalPresets.map((preset) => {
                  const Icon = PRESET_ICONS[preset.icon] || Target;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => selectGoalPreset(preset)}
                      className={`goal-preset-button ${goalData.selectedPreset === preset.id ? 'preset-active' : ''}`}
                      data-testid={`goal-preset-${preset.id}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{preset.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="input-label">Nombre de tu meta</label>
              <Input
                type="text"
                placeholder="Mi auto soñado"
                value={goalData.goalName}
                onChange={(e) => setGoalData({ ...goalData, goalName: e.target.value })}
                className="input-field"
                data-testid="goal-name-input"
              />
            </div>

            <div>
              <label className="input-label">Monto objetivo ($)</label>
              <Input
                type="number"
                placeholder="200000"
                value={goalData.goalAmount}
                onChange={(e) => setGoalData({ ...goalData, goalAmount: e.target.value })}
                className="input-field"
                data-testid="goal-amount-input"
              />
            </div>

            <div>
              <label className="input-label">Ahorro actual ($)</label>
              <Input
                type="number"
                placeholder="20000"
                value={goalData.currentSavings}
                onChange={(e) => setGoalData({ ...goalData, currentSavings: e.target.value })}
                className="input-field"
                data-testid="goal-current-savings-input"
              />
            </div>

            <div>
              <label className="input-label">Aporte mensual ($)</label>
              <Input
                type="number"
                placeholder="3000"
                value={goalData.monthlyContribution}
                onChange={(e) => setGoalData({ ...goalData, monthlyContribution: e.target.value })}
                className="input-field"
                data-testid="goal-monthly-input"
              />
            </div>

            <div>
              <label className="input-label">Rendimiento anual estimado (%)</label>
              <Input
                type="number"
                placeholder="8"
                value={goalData.rate}
                onChange={(e) => setGoalData({ ...goalData, rate: e.target.value })}
                className="input-field"
                data-testid="goal-rate-input"
              />
            </div>

            <Button onClick={calculateGoal} className="button-neon w-full" data-testid="goal-calculate-button">
              <Calculator className="w-4 h-4 mr-2" />
              Hazme las cuentas crudas
            </Button>
          </div>

          <div className="goal-results">
            {goalData.yearsNeeded !== null ? (
              <div className="goal-result-card">
                <Sparkles className="w-12 h-12 text-neon mx-auto mb-4" />
                <h3 className="goal-result-title">¡Listo! Aquí está tu plan</h3>

                <div className="goal-result-stats">
                  <div className="goal-stat">
                    <Calendar className="w-6 h-6 text-neon" />
                    <div>
                      <p className="goal-stat-label">Tiempo necesario</p>
                      <p className="goal-stat-value">
                        {goalData.yearsNeeded < 1
                          ? `${goalData.monthsNeeded} meses`
                          : `${goalData.yearsNeeded} años`}
                      </p>
                    </div>
                  </div>

                  <div className="goal-stat">
                    <Target className="w-6 h-6 text-neon" />
                    <div>
                      <p className="goal-stat-label">Total a aportar</p>
                      <p className="goal-stat-value">
                        ${(parseFloat(goalData.monthlyContribution) * goalData.monthsNeeded).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="goal-stat highlight">
                    <Shield className="w-6 h-6 text-neon" />
                    <div>
                      <p className="goal-stat-label">Rendimiento ganado</p>
                      <p className="goal-stat-value text-neon">
                        ${(parseFloat(goalData.goalAmount) - parseFloat(goalData.currentSavings) - (parseFloat(goalData.monthlyContribution) * goalData.monthsNeeded)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Comparison: With vs Without compound interest */}
                {(() => {
                  const goal = parseFloat(goalData.goalAmount);
                  const current = parseFloat(goalData.currentSavings);
                  const monthly = parseFloat(goalData.monthlyContribution);
                  const remaining = goal - current;
                  const monthsNoInterest = remaining > 0 && monthly > 0 ? remaining / monthly : 0;
                  const yearsNoInterest = monthsNoInterest / 12;
                  const yearsWith = parseFloat(goalData.yearsNeeded);
                  const monthsSaved = Math.max(0, Math.ceil(monthsNoInterest - goalData.monthsNeeded));
                  // Bar widths normalized to the longer one
                  const maxYears = Math.max(yearsWith, yearsNoInterest, 0.01);
                  const widthWith = (yearsWith / maxYears) * 100;
                  const widthWithout = (yearsNoInterest / maxYears) * 100;

                  return (
                    <div className="goal-comparison" data-testid="goal-comparison">
                      <div className="goal-comparison-header">
                        <Zap className="w-4 h-4" />
                        <span>Por qué INVERTIR le gana a solo AHORRAR</span>
                      </div>
                      <div className="comparison-row comparison-row-good">
                        <div className="comparison-row-top">
                          <span className="comparison-label">
                            <Sparkles className="w-3.5 h-3.5" />
                            Invirtiendo ({goalData.rate}% anual)
                          </span>
                          <span className="comparison-time comparison-time-good">
                            {yearsWith < 1 ? `${goalData.monthsNeeded} meses` : `${yearsWith} años`}
                          </span>
                        </div>
                        <div className="comparison-bar-track">
                          <div className="comparison-bar comparison-bar-good" style={{ width: `${widthWith}%` }} />
                        </div>
                      </div>
                      <div className="comparison-row comparison-row-bad">
                        <div className="comparison-row-top">
                          <span className="comparison-label">
                            <TrendingDown className="w-3.5 h-3.5" />
                            Sólo guardando bajo el colchón
                          </span>
                          <span className="comparison-time comparison-time-bad">
                            {yearsNoInterest < 1
                              ? `${Math.ceil(monthsNoInterest)} meses`
                              : `${yearsNoInterest.toFixed(1)} años`}
                          </span>
                        </div>
                        <div className="comparison-bar-track">
                          <div className="comparison-bar comparison-bar-bad" style={{ width: `${widthWithout}%` }} />
                        </div>
                      </div>
                      {monthsSaved > 0 && (
                        <p className="comparison-savings">
                          Te ahorras <strong>{monthsSaved} {monthsSaved === 1 ? 'mes' : 'meses'}</strong> de tu vida 💸
                          {monthsSaved >= 12 && ' (sí, leíste bien)'}
                        </p>
                      )}
                    </div>
                  );
                })()}

                <div className="goal-cta">
                  <p className="text-sm text-gray-400 mb-3">
                    Plan listo. Ahora la parte difícil: ejecutar. (Tranqui, podemos automatizarlo por ti) 🤖
                  </p>
                  <Button
                    className="button-neon w-full"
                    onClick={() => scrollToSection('email-capture')}
                    data-testid="goal-cta-button"
                  >
                    Quítamelo antes de gastármelo
                  </Button>
                </div>
              </div>
            ) : (
              <div className="goal-result-placeholder">
                <Target className="w-24 h-24 text-gray-700 mb-4" />
                <p className="text-gray-400 text-center">
                  Llena los campos y enfrenta la realidad. No muerde (mucho).
                </p>
              </div>
            )}
          </div>
        </div>

        {goalData.yearsNeeded !== null && (
          <InstitutionRecommendations
            years={parseFloat(goalData.yearsNeeded)}
            source="goal"
            onCtaClick={() => scrollToSection('email-capture')}
          />
        )}
      </CardContent>
    </Card>
  </div>
);

export default GoalCalculatorCard;
